import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, flatMap, tap } from 'rxjs/operators';
import { GroupsService } from '../services/groups.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    template: `
        <div fxLayout="column">
            <mat-toolbar>
                <h3>Edit Group</h3>
                <span fxFlex></span>
            </mat-toolbar>
            <div style="height: calc(100vh - 132px); overflow-y: auto;">
                <mat-card fxLayoutAlign="space-around start">
                    <mat-card-content [formGroup]="form" fxFlex fxLayout="column" style="padding: 20px;">
                        <mat-tab-group>
                            <mat-tab label="Settings">
                                <div fxLayout="row" style="padding-top: 10px;">
                                    <span fxFlex></span>
                                    <button mat-raised-button color="primary" [disabled]="form.invalid || isUnchanged" (click)="save()">Save</button>
                                </div>
                                <group-editor-basic [form]="form" [allowAutoAddNewUsers]="true"></group-editor-basic>
                            </mat-tab>
                            <mat-tab label="Members">
                                <group-members [group]="group" (membersChanged)="refetchGroup()"></group-members>
                            </mat-tab>
                            <mat-tab label="Topics">
                                <group-topics [group]="group" (subscriptionsChanged)="refetchGroup()"></group-topics>
                            </mat-tab>
                            <mat-tab label="Briefings">
                                <group-briefings [group]="group"></group-briefings>
                            </mat-tab>
                        </mat-tab-group>
                    </mat-card-content>
                </mat-card>
            </div>            
        </div>
    `
})
export class GroupEditComponent implements OnInit, OnDestroy {
    private cleanupTime = new Subject<void>();
    private readonly attrs = ['name', 'description', 'autoAddNewUsers', 'allowUsersToJoin'];
    form: FormGroup;
    group: any;
    isUnchanged: boolean;

    constructor(private fb: FormBuilder, 
                private route: ActivatedRoute,
                private router: Router,
                private snackBar: MatSnackBar, 
                private groupService: GroupsService) {}    

    ngOnInit(): void {
        this.form = this.fb.group({ 
            name: ['', Validators.required], 
            description: [],
            autoAddNewUsers: [],
            allowUsersToJoin: [false], 
        });


        this.form.valueChanges.pipe(takeUntil(this.cleanupTime))
        .subscribe(() => {
            this.isUnchanged = this.attrs.reduce((accum, key) => 
                accum && ((!this.group[key] && !this.form.get(key).value) || this.group[key] === this.form.get(key).value), true);
        });

        this.route.params
        .pipe(
            flatMap(params => this.groupService.fetchGroup(params.groupId)),
            tap(() => this.isUnchanged = true),
            tap(group => this.group = group),
            takeUntil(this.cleanupTime)
        ).subscribe(group => this.form.patchValue(group));
    }

    save() {
        this.group.name = this.form.get('name').value;
        this.group.description = this.form.get('description').value;
        this.group.autoAddNewUsers = this.form.get('autoAddNewUsers').value;
        this.group.allowUsersToJoin = this.form.get('allowUsersToJoin').value;
        this.form.disable();
        this.groupService.updateGroup(this.group)
        .subscribe(
            () => {
                this.snackBar.open(`${this.group.name} saved.`, 'OK', { duration: 3000});
                this.router.navigate(['../'], { relativeTo: this.route });
                this.form.enable();
            },
            () => {
                this.form.enable();
                this.snackBar.open(`Unable to save ${this.group.name}, please try again.`, 'OK', { duration: 3000});
            }
        )
    }

    refetchGroup() {
        this.groupService.fetchGroup(this.group.id).subscribe(group =>  this.group = group);
    }

    ngOnDestroy() {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}